import { McpSearchScopeTabs } from "@arcadeai/design-system/components/ui/molecules/mcp-server-picker/search-scope-tabs";
import { McpSelectionSummaryBar } from "@arcadeai/design-system/components/ui/organisms/mcp-selection-summary-bar";
import { McpServerPicker as McpServerPickerShell } from "@arcadeai/design-system/components/ui/organisms/mcp-server-picker";
import { cn } from "@arcadeai/design-system/lib/utils";
import { useState } from "react";
import { PickerBody } from "./components/picker-body";
import { PickerHeader } from "./components/picker-header";
import { useMcpServerPicker } from "./hooks/use-mcp-server-picker";
import type { McpServer, Mode } from "./types";

export type McpServerPickerProps = {
  /**
   * The MCP servers to render. Each `name` must be unique across the array —
   * it is the server's identity (React key, selection-count key, drill-in
   * target). Duplicate names collapse in lookups and collide as keys.
   *
   * Pass a referentially stable array (e.g. straight from your query cache);
   * the hook memoizes derivations on this reference, so a fresh array on every
   * render re-runs filtering.
   */
  servers: McpServer[];
  /**
   * Controlled selection (FQNs). When provided, the caller owns selection state
   * and should update this prop from `onChange`.
   */
  selectedFqns?: string[];
  /**
   * Initial uncontrolled selection (FQNs). Ignored when `selectedFqns` is
   * provided. Stale entries are reconciled silently on mount; `onChange` is
   * not fired for that initial cleanup in uncontrolled mode.
   */
  defaultSelectedFqns?: string[];
  /**
   * Fires when the effective selection changes from user actions or when the
   * catalog drops previously selected tools (controlled mode, or uncontrolled
   * after mount). The array is ordered by selection (insertion) order, not
   * catalog order; treat it as a set.
   */
  onChange?: (selectedFqns: string[]) => void;
  /** @default "multi" */
  mode?: Mode;
  /** Additional classes applied to the outermost shell element. */
  className?: string;
};

export function McpServerPicker({
  servers,
  selectedFqns,
  defaultSelectedFqns,
  onChange,
  mode = "multi",
  className,
}: McpServerPickerProps) {
  const { state, actions } = useMcpServerPicker({
    servers,
    selectedFqns,
    defaultSelectedFqns,
    mode,
    onChange,
  });

  // The scroll viewport is the virtualizer's scroll element. Captured via state
  // (not a ref) so the grids re-render once it mounts and can start virtualizing.
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);

  return (
    <McpServerPickerShell className={cn("flex h-full flex-col", className)}>
      <PickerHeader actions={actions} mode={mode} state={state} />

      <McpSelectionSummaryBar
        mcpServers={state.summaryChips}
        onClear={actions.clearSelection}
        onOpenMcpServer={actions.openTools}
        onRemoveMcpServer={actions.removeServer}
        onReview={actions.reviewSelection}
        reviewing={state.view.type === "review"}
        toolCount={state.selectedToolCount}
      />

      {state.view.type === "servers" && state.hasSearch ? (
        <McpSearchScopeTabs
          active={state.searchScope}
          onChange={actions.setSearchScope}
          serverCount={state.filteredServers.length}
          toolCount={state.filteredToolCount}
        />
      ) : null}

      <div
        className="scroll-fade min-h-0 flex-1 space-y-3 overflow-y-auto pr-1.5"
        ref={setScrollEl}
      >
        <PickerBody
          actions={actions}
          mode={mode}
          scrollContainer={scrollEl}
          state={state}
        />
      </div>
    </McpServerPickerShell>
  );
}
